/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

export interface PiiRedactionOptions {
  redactEmails?: boolean;
  redactPhones?: boolean;
  redactSSN?: boolean;
  redactRoutingNumbers?: boolean;
}

export interface PiiRedactionResult {
  text: string;
  redacted: string[];
}

const EMAIL_REGEX = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE_REGEX = /\+?\d[\d\s().-]{6,}\d/g;
const SSN_REGEX = /\b\d{3}-\d{2}-\d{4}\b/g;
/** Labeled US ABA routing numbers in free text (align with pii-detector) */
const ROUTING_NUMBER_REGEX = /\brouting(?:\s+number)?\s*[:#]?\s*\d{9}\b/gi;

export function redactPII(
  text: string,
  options: PiiRedactionOptions = {},
): PiiRedactionResult {
  const cfg = {
    redactEmails: options.redactEmails ?? true,
    redactPhones: options.redactPhones ?? true,
    redactSSN: options.redactSSN ?? true,
    redactRoutingNumbers: options.redactRoutingNumbers ?? true,
  };

  const redacted: string[] = [];
  let out = text;

  if (cfg.redactEmails) {
    const emailMatches = text.match(EMAIL_REGEX);
    if (emailMatches && emailMatches.length > 0) {
      redacted.push(...emailMatches.map(() => "email"));
    }
    out = out.replace(EMAIL_REGEX, "[REDACTED_EMAIL]");
  }

  // Redact SSNs before phones so SSN-like patterns are not consumed by the phone regex
  if (cfg.redactSSN) {
    const ssnMatches = text.match(SSN_REGEX);
    if (ssnMatches && ssnMatches.length > 0) {
      redacted.push(...ssnMatches.map(() => "ssn"));
    }
    out = out.replace(SSN_REGEX, "[REDACTED_SSN]");
  }

  if (cfg.redactRoutingNumbers) {
    const routingMatches = out.match(ROUTING_NUMBER_REGEX);
    if (routingMatches && routingMatches.length > 0) {
      redacted.push(...routingMatches.map(() => "routing"));
    }
    out = out.replace(ROUTING_NUMBER_REGEX, "[REDACTED_ROUTING]");
  }

  if (cfg.redactPhones) {
    const phoneMatches = text.match(PHONE_REGEX);
    if (phoneMatches && phoneMatches.length > 0) {
      redacted.push(...phoneMatches.map(() => "phone"));
    }
    out = out.replace(PHONE_REGEX, "[REDACTED_PHONE]");
  }

  return { text: out, redacted };
}
